import { zodResolver } from '@hookform/resolvers/zod';
import type { Shelf, Branch, Scanner } from '@packages/shared-types';
import { Pencil, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { PageHeader } from '@/components/shared/PageHeader';
import { TableCard } from '@/components/shared/TableCard';
import { TablePagination } from '@/components/shared/TablePagination';
import { TableActionsMenu, type TableAction } from '@/components/shared/TableActionsMenu';
import { useAuth } from '@/hooks/useAuth';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import { usePaginationFromUrl } from '@/hooks/usePaginationFromUrl';
import {
  listShelvesPaginated,
  createShelf,
  type CreateShelfPayload,
} from '@/services/shelfService';
import { listBranches } from '@/services/branchService';
import { listScanners } from '@/services/scannerService';

const shelfFormSchema = z.object({
  name: z.string().min(1, 'pages.shelves.form.nameRequired'),
  branchId: z.string().uuid().min(1, 'pages.shelves.form.branchRequired'),
  scannerId: z.string().uuid().optional().or(z.literal('')),
  zone: z.string().optional(),
  aisle: z.string().optional(),
  module: z.string().optional(),
  row: z.string().optional(),
  cell: z.string().optional(),
});

type ShelfFormValues = z.infer<typeof shelfFormSchema>;

export function ShelvesPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'root' || user?.role === 'admin';
  const title = useIndustryTranslation('pages.shelves.title');
  const description = useIndustryTranslation('pages.shelves.description');
  const createLabel = useIndustryTranslation('pages.shelves.actions.create');
  const emptyLabel = useIndustryTranslation('pages.shelves.list.empty');
  const loadingLabel = useIndustryTranslation('pages.shelves.list.loading');
  const successCreate = useIndustryTranslation('pages.shelves.success.create');
  const errorLoad = useIndustryTranslation('pages.shelves.error.load');
  const errorCreate = useIndustryTranslation('pages.shelves.error.create');

  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [scanners, setScanners] = useState<Scanner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isLoadingScanners, setIsLoadingScanners] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const pagination = usePaginationFromUrl();

  const {
    register: registerCreate,
    handleSubmit: handleSubmitCreate,
    control: controlCreate,
    watch: watchCreate,
    setValue: setValueCreate,
    formState: { errors: createErrors, isSubmitting: isCreating },
    reset: resetCreate,
  } = useForm<ShelfFormValues>({
    resolver: zodResolver(shelfFormSchema),
    defaultValues: {
      name: '',
      branchId: '',
      scannerId: '',
      zone: '',
      aisle: '',
      module: '',
      row: '',
      cell: '',
    },
  });

  const selectedBranchId = watchCreate('branchId');

  // Filter scanners by selected branch
  const availableScanners = useMemo(() => {
    if (!selectedBranchId) return [];
    return scanners.filter((scanner) => scanner.branchId === selectedBranchId);
  }, [scanners, selectedBranchId]);

  const loadShelves = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await listShelvesPaginated({
        page: pagination.page,
        limit: pagination.limit,
      });
      setShelves(result.data);
      pagination.setTotal(result.total);
    } catch (err) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : errorLoad;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [errorLoad, pagination.page, pagination.limit]);

  useEffect(() => {
    void loadShelves();
  }, [loadShelves]);

  // Fetch branches
  useEffect(() => {
    const loadBranches = async (): Promise<void> => {
      setIsLoadingBranches(true);
      try {
        const data = await listBranches();
        setBranches(data);
      } catch (err) {
        console.error('Failed to load branches', err);
      } finally {
        setIsLoadingBranches(false);
      }
    };
    void loadBranches();
  }, []);

  // Fetch scanners
  useEffect(() => {
    const loadScanners = async (): Promise<void> => {
      setIsLoadingScanners(true);
      try {
        const data = await listScanners();
        setScanners(data);
      } catch (err) {
        console.error('Failed to load scanners', err);
      } finally {
        setIsLoadingScanners(false);
      }
    };
    void loadScanners();
  }, []);

  const handleCreate = async (values: ShelfFormValues): Promise<void> => {
    try {
      const payload: CreateShelfPayload = {
        name: values.name,
        branchId: values.branchId,
        scannerId: values.scannerId || undefined,
        zone: undefined,
        aisle: undefined,
        module: undefined,
        row: undefined,
        cell: undefined,
      };
      const newShelf = await createShelf(payload);
      setIsCreateDialogOpen(false);
      resetCreate();
      toast.success(successCreate);
      navigate(`/shelves/${newShelf.id}/edit`);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : errorCreate;
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const formatDate = (date: Date | string): string => {
    return new Date(date).toLocaleDateString();
  };

  const handleRowClick = useCallback(
    (shelfId: string) => {
      return () => navigate(`/shelves/${shelfId}/edit`);
    },
    [navigate],
  );

  const handleEditClick = useCallback(
    (shelfId: string) => {
      return () => navigate(`/shelves/${shelfId}/edit`);
    },
    [navigate],
  );

  const createDialogTitle = useIndustryTranslation(
    'pages.shelves.dialogs.create.title',
  );
  const createDialogDescription = useIndustryTranslation(
    'pages.shelves.dialogs.create.description',
  );
  const createAddDetailsLater = t(
    'pages.shelves.dialogs.create.addDetailsLater',
  );
  const branchLabel = useIndustryTranslation('pages.shelves.form.branch');
  const selectBranchLabel = t('pages.shelves.form.selectBranch');
  const scannerLabel = t('pages.shelves.form.scanner');
  const selectScannerLabel = t('pages.shelves.form.selectScanner');
  const branchTableLabel = useIndustryTranslation('pages.shelves.table.branch');

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={title}
        description={description}
        actions={
          isAdmin ? (
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              {createLabel}
            </Button>
          ) : undefined
        }
      />

      {error && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="p-4">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      <TableCard>
        {isLoading ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <LoadingLogo size="sm" />
            <span className="text-sm text-muted-foreground">{loadingLabel}</span>
          </div>
        ) : shelves.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {emptyLabel}
          </div>
        ) : (
          <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('pages.shelves.table.name')}</TableHead>
                <TableHead>{branchTableLabel}</TableHead>
                <TableHead>{t('pages.shelves.table.zone')}</TableHead>
                <TableHead>{t('pages.shelves.table.scanner')}</TableHead>
                <TableHead>{t('pages.shelves.table.created')}</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shelves.map((shelf) => {
                const branch = branches.find((b) => b.id === shelf.branchId);
                const scanner = scanners.find((s) => s.id === shelf.scannerId);
                const actions: TableAction[] = isAdmin
                  ? [
                      {
                        label: t('pages.shelves.actions.edit'),
                        icon: Pencil,
                        onClick: handleEditClick(shelf.id),
                      },
                    ]
                  : [];
                return (
                  <TableRow
                    key={shelf.id}
                    onRowClick={handleRowClick(shelf.id)}
                  >
                    <TableCell className="font-medium">{shelf.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {branch?.name || '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {shelf.zone || (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {scanner?.name || (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(shelf.createdAt)}
                    </TableCell>
                    <TableCell>
                      <TableActionsMenu actions={actions} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <TablePagination
            pagination={pagination}
            rowsPerPageLabel={t('pages.common.pagination.rowsPerPage')}
            pageOfLabel={t('pages.common.pagination.pageOf', {
              current: pagination.page,
              total: pagination.totalPages,
            })}
            onRefresh={() => void loadShelves()}
            isRefreshing={isLoading}
          />
          </>
        )}
      </TableCard>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{createDialogTitle}</DialogTitle>
            <DialogDescription>{createDialogDescription}</DialogDescription>
            <p className="text-sm text-muted-foreground">
              {createAddDetailsLater}
            </p>
          </DialogHeader>
          <form onSubmit={handleSubmitCreate(handleCreate)}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="create-name">
                  {t('pages.shelves.form.name')} *
                </Label>
                <Input
                  id="create-name"
                  {...registerCreate('name')}
                  placeholder={t('pages.shelves.form.name')}
                />
                {createErrors.name && (
                  <p className="text-sm text-destructive">
                    {t(createErrors.name.message || 'pages.shelves.form.nameRequired')}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-branch">
                  {branchLabel} *
                </Label>
                <Controller
                  name="branchId"
                  control={controlCreate}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        // Reset scanner when branch changes
                        setValueCreate('scannerId', '');
                      }}
                      disabled={isLoadingBranches}
                    >
                      <SelectTrigger id="create-branch">
                        <SelectValue placeholder={selectBranchLabel} />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.map((branch) => (
                          <SelectItem key={branch.id} value={branch.id}>
                            {branch.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {createErrors.branchId && (
                  <p className="text-sm text-destructive">
                    {t(createErrors.branchId.message || 'pages.shelves.form.branchRequired')}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-scanner">
                  {scannerLabel} (optional)
                </Label>
                <Controller
                  name="scannerId"
                  control={controlCreate}
                  render={({ field }) => (
                    <Select
                      value={field.value || undefined}
                      onValueChange={(value) => field.onChange(value || '')}
                      disabled={isLoadingScanners || !selectedBranchId}
                    >
                      <SelectTrigger id="create-scanner">
                        <SelectValue placeholder={selectScannerLabel} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableScanners.length === 0 ? (
                          <SelectItem value="none" disabled>
                            {selectedBranchId
                              ? t('pages.shelves.form.noScanners')
                              : t('pages.shelves.form.selectBranchFirst')}
                          </SelectItem>
                        ) : (
                          availableScanners.map((scanner) => (
                            <SelectItem key={scanner.id} value={scanner.id}>
                              {scanner.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  resetCreate();
                }}
              >
                {t('pages.shelves.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating
                  ? t('pages.shelves.actions.saving')
                  : createLabel}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
